<template>
  <div class="container-fluid px-4 mt-4" :class="{ 'merge-warn-mode': hasMultipleClients }">
    <!-- Loading ekranı -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p>Yükleniyor...</p>
    </div>

    <!-- Şık Sekmeler -->
    <div class="d-flex justify-content-center mb-4">
      <div class="tab-buttons">
        <b-button
            :variant="activeTab === 0 ? 'warning' : 'outline-warning'"
            class="tab-button"
            @click="changeTab(0)"
        >
          Onay Bekleyenler
        </b-button>

        <b-button
            :variant="activeTab === 1 ? 'success' : 'outline-success'"
            class="tab-button"
            @click="changeTab(1)"
        >
          Onaylananlar
        </b-button>

        <b-button
            :variant="activeTab === 2 ? 'danger' : 'outline-danger'"
            class="tab-button"
            @click="changeTab(2)"
        >
          İptal Edilenler
        </b-button>
      </div>
    </div>

    <!-- Liste -->
    <div class="row" v-show="!loading">
      <div class="container-fluid px-3">
        <vue-good-table
            mode="remote"
            @on-column-filter="onColumnFilter($event.columnFilters)"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange"
            @on-selected-rows-change="onSelectionChange"
            :rows="rows"
            :columns="columns"
            :totalRows="totalRecords"
            :line-numbers="true"
            :select-options="{
              enabled: activeTab === 0,
              selectionText: 'sipariş seçildi',
              clearSelectionText: 'Temizle',
              disableSelectInfo: false,
              selectOnCheckboxOnly: true
            }"
            :pagination-options="{
              enabled: true,
              perPage: serverParams.perPage,
              perPageDropdown: [20, 50, 100],
              dropdownAllowAll: false,
              mode: 'records',
              nextLabel: 'Sonraki',
              prevLabel: 'Önceki',
              rowsPerPageLabel: 'Sayfa başına satır',
              ofLabel: '/',
              pageLabel: 'sayfa',
              allLabel: 'Tümü'
            }"
        >
          <template #selected-row-actions>
            <div class="d-flex flex-row align-items-center flex-wrap merge-action">
              <a
                  v-if="!isMergeDisabled"
                  href="#"
                  class="merge-link"
                  @click.prevent="showMergeModal = true"
              >
                Seçilen Siparişleri Birleştir
              </a>
              <small v-if="hasMultipleClients" class="text-danger merge-warning">
                Birleştirme için aynı işletmeye ait siparişleri seçin.
              </small>
            </div>
          </template>
          <template #table-row="props">
            <span v-if="props.column.field === 'actions'" class="d-flex flex-wrap gap-2">
              <b-button size="sm" variant="info" @click="showOrderDetails(props.row.id, props.row.totalPrice)">
                Sipariş Detayları
              </b-button>
              <b-button v-if="activeTab === 0" size="sm" variant="success" @click="toggleApprove(props.row.id)">
                Onayla
              </b-button>
              <b-button v-if="activeTab === 0" size="sm" variant="danger" @click="toggleReject(props.row.id)">
                Reddet
              </b-button>
            </span>
            <span v-if="props.column.field === 'notes'" v-html="props.row.notes"></span>
            <span v-else>{{ props.row[props.column.field] }}</span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <b-sidebar
        id="order-detail-sidebar"
        title=""
        :visible="showSidebar"
        right
        shadow
        backdrop
        @hidden="clearSidebar"
        class="custom-sidebar"
    >
      <div class="p-3">

        <h4 class="mb-4 font-weight-bold text-dark text-center">🧾 Sipariş Detayları</h4>

        <div v-if="orderItemsLoading" class="text-center my-4">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p>Ürünler yükleniyor...</p>
        </div>

        <div v-else>
          <div
              v-for="(item, index) in orderItems"
              :key="index"
              class="mb-3 p-3 rounded shadow-sm border bg-white"
          >
            <!-- Ürün Başlığı ve Miktar -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong class="text-dark">{{ item.productName }}</strong>
              <b-badge variant="primary" pill style="font-size: 0.8rem; padding: 0.5em 0.75em;">{{ item.quantity }}
                Adet
              </b-badge>
            </div>

            <!-- Fiyatlar -->
            <div class="d-flex justify-content-between small text-muted mb-2">
              <div>Birim Fiyat: <strong>{{ item.priceText }}</strong></div>
              <div>Toplam: <strong class="text-success">{{ item.totalPriceText }}</strong></div>
            </div>

            <!-- Ürün Değiştir Butonu -->
            <div class="d-flex justify-content-end align-items-center flex-wrap gap-2" v-if="activeTab === 0">
              <div class="qty-stepper d-flex align-items-center">
                <b-button
                    size="sm"
                    variant="danger"
                    class="qty-btn"
                    :disabled="itemActionLoading[item.id]"
                    @click="decreaseQuantity(item)"
                >
                  −
                </b-button>
                <b-form-input
                    type="number"
                    min="1"
                    :max="item.quantity"
                    class="qty-input mx-1"
                    size="sm"
                    v-model="quantityDeltas[item.id]"
                    :disabled="itemActionLoading[item.id]"
                />
                <b-button
                    size="sm"
                    variant="success"
                    class="qty-btn"
                    :disabled="itemActionLoading[item.id]"
                    @click="increaseQuantity(item)"
                >
                  +
                </b-button>
              </div>
              <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="toggleEdit(index)"
              >
                Ürün Değiştir
              </b-button>
              <b-button
                  size="sm"
                  variant="outline-danger"
                  @click="toggleDelete(item.id)"
              >
                Ürün Sil
              </b-button>
            </div>

            <!-- Eğer bu satır düzenleme modundaysa -->
            <div v-if="editingIndex === index" class="mt-3">
              <b-form-group label="Yeni Ürün Seçiniz">
                <v-select
                    v-model="editForm.productId"
                    :options="products"
                    label="name"
                    placeholder="Ürün adı giriniz"
                    :filter-by="turkishFilterBy"
                    :reduce="product => product.id"
                    clearable
                />
              </b-form-group>

              <b-form-group label="Adet">
                <b-form-input type="number" min="1" v-model="editForm.quantity"/>
              </b-form-group>

              <b-button
                  size="sm"
                  variant="success"
                  @click="submitEdit(item.id)"
                  :disabled="!editForm.productId || !editForm.quantity || editForm.quantity <= 0"
              >
                Güncelle
              </b-button>
            </div>

          </div>
          <div class="d-flex justify-content-between align-items-center mt-2">
            <b-button
                size="sm"
                variant="outline-primary"
                @click="toggleAddItem"
            >
              Ürün Ekle
            </b-button>

            <div>
              Sipariş Toplamı: <strong class="text-success">{{ detailTotalPrice }}</strong>
            </div>
          </div>
          <div class="mb-3 p-3 rounded shadow-sm border bg-white" v-if="addItemToggle">
            <div
                v-for="(row, idx) in addItems"
                :key="idx"
                class="add-item-row mb-3 pb-2"
                :class="{ 'border-bottom': idx < addItems.length - 1 }"
            >
              <div class="d-flex justify-content-between align-items-center mb-1">
                <strong>Ürün {{ idx + 1 }}</strong>
                <b-button
                    v-if="addItems.length > 1"
                    size="sm"
                    variant="outline-danger"
                    @click="removeAddItemRow(idx)"
                >
                  Satırı Sil
                </b-button>
              </div>
              <b-form-group label="Eklemek İstediğiniz Ürünü Seçiniz">
                <v-select
                    v-model="row.productId"
                    :options="products"
                    label="name"
                    placeholder="Ürün adı giriniz"
                    :filter-by="turkishFilterBy"
                    :reduce="product => product.id"
                    clearable
                />
              </b-form-group>

              <b-form-group label="Adet">
                <b-form-input type="number" min="1" v-model="row.quantity"/>
              </b-form-group>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="addAddItemRow"
                  :disabled="!canAddAnotherRow"
              >
                + Satır Ekle
              </b-button>
              <b-button
                  size="sm"
                  variant="success"
                  @click="addOrderItem"
                  :disabled="!hasValidAddItem"
              >
                Siparişe Ürün Ekle
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </b-sidebar>
    <b-modal v-model="showDeleteModal" title="Ürünü Sil"
             ok-title="Evet, Sil"
             cancel-title="Vazgeç"
             ok-variant="danger"
             @ok="removeOrderItem">
      <p class="my-2">Bu ürünü siparişten silmek istediğinize emin misiniz ?</p>
    </b-modal>
    <b-modal v-model="showApproveModal" title="Siparişi Onayla"
             ok-title="Onayla"
             cancel-title="Vazgeç"
             ok-variant="success"
             @ok="approve">
      <p class="my-2">Bu siparişi onaylamak istediğinize emin misiniz ?</p>
    </b-modal>
    <b-modal v-model="showRejectModal" title="Siparişi İptal Et"
             ok-title="İptal Et"
             cancel-title="Vazgeç"
             ok-variant="danger"
             @ok="reject">
      <p class="my-2">Bu siparişi iptal etmek istediğinize emin misiniz ?</p>
    </b-modal>
    <b-modal v-model="showMergeModal" title="Siparişleri Birleştir"
             ok-title="Birleştir"
             cancel-title="Vazgeç"
             ok-variant="primary"
             @ok="mergeOrders">
      <p class="my-2">Seçili {{ selectedRows.length }} siparişi birleştirmek istediğinize emin misiniz ?</p>
    </b-modal>
  </div>
</template>

<script>
import OrderService from '../../service/OrderService';
import ProductService from '../../service/ProductService';
import { turkishFilterBy } from '../../utils/TurkishSearchUtils';
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
  name: 'admin-order-list',
  components: {
    'v-select': vSelect
  },
  data() {
    return {
      rows: [],
      columns: [
        { field: 'id', hidden: true },
        { label: 'İşletme', field: 'clientName', sortable: false },
        { label: 'Sipariş Zamanı', field: 'orderDate', sortable: false },
        { label: 'Sipariş Tutarı', field: 'totalPrice', sortable: false },
        { label: 'Notlar', field: 'notes', sortable: false, tdClass: 'col-notes', thClass: 'col-notes'  },
        { label: 'İşlem', field: 'actions', sortable: false }
      ],
      orderItems: [],
      orderItemFields: [
        { key: 'productName', label: 'Ürün Adı' },
        { key: 'quantity', label: 'Adet' },
        { key: 'priceText', label: 'Birim Fiyat' },
        { key: 'totalPriceText', label: 'Toplam Tutar' }
      ],
      showModal: false,
      loading: false,
      orderItemsLoading: false,
      activeTab: 0,
      totalRecords: 0,
      showSidebar: false,
      detailTotalPrice: '',
      orderId: null,
      editingIndex: null,
      addItemToggle: false,
      editForm: {
        productId: null,
        quantity: 1,
      },
      addItems: [
        { productId: null, quantity: 1 },
      ],
      products: [], // Tüm muadil ürünlerin listesi
      searchTerm: '',
      serverParams: {
        columnFilters: {},
        sort: {
          field: '',
          type: '',
        },
        page: 1,
        perPage: 20
      },
      removeOrderItemId: undefined,
      approveOrderId: undefined,
      rejectOrderId: undefined,
      showDeleteModal: false,
      showApproveModal: false,
      showRejectModal: false,
      showMergeModal: false,
      selectedRows: [],
      quantityDeltas: {},
      itemActionLoading: {},
    };
  },
  computed: {
    hasMultipleClients() {
      if (!this.selectedRows || this.selectedRows.length === 0) {
        return false;
      }
      const firstClient = this.selectedRows[0].clientName;
      return this.selectedRows.some(row => row.clientName !== firstClient);
    },
    isMergeDisabled() {
      return this.selectedRows.length < 2 || this.hasMultipleClients;
    },
    hasValidAddItem() {
      return this.addItems.some(row => row.productId && Number(row.quantity) > 0);
    },
    canAddAnotherRow() {
      const lastRow = this.addItems[this.addItems.length - 1];
      return !!(lastRow && lastRow.productId);
    },
  },
  methods: {
    turkishFilterBy,
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps);
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadOrders(this.serverParams);
    },

    onPerPageChange(params) {
      this.updateParams({ perPage: params.currentPerPage });
      this.loadOrders(this.serverParams);
    },

    onColumnFilter(params) {
      this.updateParams({ columnFilters: params });
      this.loadOrders(this.serverParams);
    },

    async loadOrders() {
      this.loading = true;
      this.selectedRows = [];
      const status =
          this.activeTab === 0 ? 'CREATED' :
          this.activeTab === 1 ? 'APPROVED' : 'REJECTED';
      try {
        this.serverParams.status = status;
        const response = await OrderService.getOrders(this.serverParams);
        this.rows = response.data;
        this.totalRecords = response.total;
      } catch (error) {
        console.error("Siparişler yüklenirken hata oluştu:", error);
        this.$errorNotification(this, "Siparişler görüntülenirken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },
    changeTab(index) {
      if (this.activeTab !== index) {
        this.activeTab = index;
        this.loadOrders();
      }
    },
    async showOrderDetails(orderId, totalPrice) {
      this.orderItemsLoading = true;
      this.showSidebar = true;
      this.detailTotalPrice = totalPrice;
      this.orderId = orderId;
      try {
        this.orderItems = await OrderService.getOrderItems(orderId);
      } catch (error) {
        console.error("Sipariş detayları yüklenirken hata oluştu:", error);
        this.$errorNotification(this, "Sipariş detayları yüklenirken hata oluştu.");
      } finally {
        this.orderItemsLoading = false;
      }
    },
    clearSidebar() {
      this.showSidebar = false;
      this.orderItems = [];
      this.detailTotalPrice = '';
      this.orderId = null;
      this.editingIndex = null;
      this.addItemToggle = false;
      this.addItems = [{ productId: null, quantity: 1 }];
      this.quantityDeltas = {};
      this.itemActionLoading = {};
    },
    async refreshOrderDetails() {
      this.editingIndex = null;
      this.addItemToggle = false;
      this.addItems = [{ productId: null, quantity: 1 }];
      this.quantityDeltas = {};
      const orderId = this.orderId;
      await this.loadOrders();
      const updatedRow = this.rows.find(r => r.id === orderId);
      const newTotal = updatedRow ? updatedRow.totalPrice : this.detailTotalPrice;
      if (orderId) {
        await this.showOrderDetails(orderId, newTotal);
      }
    },
    async toggleApprove(orderId) {
      this.approveOrderId = orderId;
      this.showApproveModal = true;
    },
    async approve() {
      this.loading = true;
      try {
        await OrderService.approve(this.approveOrderId);
        await this.loadOrders();
        this.$notification(this, "Sipariş başarıyla onaylandı.");
      } catch (error) {
        console.error("Sipariş onaylanırken hata oluştu:", error);
        this.$errorNotification(this, "Sipariş onaylanırken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },
    async toggleReject(orderId) {
      this.rejectOrderId = orderId;
      this.showRejectModal = true;
    },
    async reject() {
      this.loading = true;
      try {
        await OrderService.reject(this.rejectOrderId);
        await this.loadOrders();
        this.$notification(this, "Sipariş iptal edildi.");
      } catch (error) {
        console.error("Sipariş reddedilirken hata oluştu:", error);
        this.$errorNotification(this, "Sipariş reddedilirken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },
    onSelectionChange(params) {
      this.selectedRows = params.selectedRows || [];
    },
    async mergeOrders() {
      if (this.isMergeDisabled) {
        return;
      }
      this.loading = true;
      try {
        const orderIds = this.selectedRows.map(row => row.id);
        await OrderService.merge({ orderIds });
        this.selectedRows = [];
        this.clearSidebar();
        await this.loadOrders();
        this.$notification(this, "Siparişler başarıyla birleştirildi.");
      } catch (error) {
        console.error("Siparişler birleştirilirken hata oluştu:", error);
        this.$errorNotification(this, "Siparişler birleştirilirken hata oluştu.");
      } finally {
        this.loading = false;
      }
    },
    async toggleEdit(index) {
      if (this.editingIndex === index) {
        this.editingIndex = null;
      } else {
        this.editingIndex = index;
      }
      this.editForm.productId = null;
      this.editForm.quantity = null;
    },
    async submitEdit(orderItemId) {
      const payload = {
        orderId: this.orderId,
        orderItemId,
        productId: this.editForm.productId,
        quantity: this.editForm.quantity,
      };

      try {
        await OrderService.updateOrderItem(payload);
        this.$notification(this, "Ürün başarıyla güncellendi.");
        //this.showOrderDetails(this.currentOrderId, this.detailTotalPrice); // Refresh
      } catch (error) {
        this.$errorNotification(this, "Ürün güncellenirken hata oluştu.");
      }
      await this.refreshOrderDetails();
    },
    async toggleDelete(orderItemId) {
      this.removeOrderItemId = orderItemId;
      this.showDeleteModal = true;
    },
    async toggleAddItem() {
      this.addItemToggle = !this.addItemToggle;
      this.addItems = [{ productId: null, quantity: 1 }];
    },
    addAddItemRow() {
      if (!this.canAddAnotherRow) {
        return;
      }
      this.addItems.push({ productId: null, quantity: 1 });
    },
    removeAddItemRow(index) {
      if (this.addItems.length <= 1) {
        return;
      }
      this.addItems.splice(index, 1);
    },
    async addOrderItem() {
      // Geçerli satırları filtrele ve aynı productId'leri sessizce birleştir
      const merged = {};
      for (const row of this.addItems) {
        const productId = row.productId;
        const quantity = Number(row.quantity);
        if (!productId || !quantity || quantity <= 0) {
          continue;
        }
        merged[productId] = (merged[productId] || 0) + quantity;
      }
      const items = Object.keys(merged).map(productId => ({
        productId,
        quantity: merged[productId],
      }));

      if (items.length === 0) {
        this.$errorNotification(this, "Eklenecek geçerli ürün bulunmamaktadır.");
        return;
      }

      const payload = {
        orderId: this.orderId,
        items,
      };

      try {
        await OrderService.addOrderItems(payload);
        this.$notification(this, "Ürünler başarıyla eklendi.");
      } catch (error) {
        this.$errorNotification(this, "Ürünler eklenirken hata oluştu.");
      }
      await this.refreshOrderDetails();
    },
    async removeOrderItem() {
      const payload = {
        orderId: this.orderId,
        orderItemId: this.removeOrderItemId
      };

      try {
        await OrderService.removeOrderItem(payload);
        this.$notification(this, "Ürün başarıyla silindi.");
        //this.showOrderDetails(this.currentOrderId, this.detailTotalPrice); // Refresh
      } catch (error) {
        this.$errorNotification(this, "Ürün güncellenirken hata oluştu.");
      }
      await this.refreshOrderDetails();
    },
    async increaseQuantity(item) {
      const delta = Number(this.quantityDeltas[item.id]) || 1;
      if (delta <= 0) {
        this.$errorNotification(this, "Geçerli bir adet giriniz.");
        return;
      }
      this.$set(this.itemActionLoading, item.id, true);
      try {
        await OrderService.increaseOrderItem({
          orderId: this.orderId,
          orderItemId: item.id,
          quantity: delta,
        });
        this.$notification(this, "Ürün adedi artırıldı.");
        await this.refreshOrderDetails();
      } catch (error) {
        this.$errorNotification(this, "Ürün adedi artırılırken hata oluştu.");
      } finally {
        this.$set(this.itemActionLoading, item.id, false);
      }
    },
    async decreaseQuantity(item) {
      const delta = Number(this.quantityDeltas[item.id]) || 1;
      if (delta <= 0) {
        this.$errorNotification(this, "Geçerli bir adet giriniz.");
        return;
      }
      if (delta > item.quantity) {
        this.$errorNotification(this, "Mevcut adetten fazla azaltılamaz.");
        return;
      }
      if (delta === item.quantity) {
        this.toggleDelete(item.id);
        return;
      }
      this.$set(this.itemActionLoading, item.id, true);
      try {
        await OrderService.decreaseOrderItem({
          orderId: this.orderId,
          orderItemId: item.id,
          quantity: delta,
        });
        this.$notification(this, "Ürün adedi azaltıldı.");
        await this.refreshOrderDetails();
      } catch (error) {
        this.$errorNotification(this, "Ürün adedi azaltılırken hata oluştu.");
      } finally {
        this.$set(this.itemActionLoading, item.id, false);
      }
    },
    async fetchProducts() {
      const requestParams = {
        columnFilters: {},
        sort: { field: '', type: '' },
        page: 1,
        perPage: 99999
      };

      try {
        const response = await ProductService.getAdminList(requestParams);
        this.products = response.data.map(product => ({
          id: product.id,
          name: `${product.name} (${product.priceText})`
        }));
      } catch (err) {
        console.error('Ürün arama hatası:', err);
      }
    },
  },
  mounted() {
    this.loadOrders();
    this.fetchProducts();
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.tab-buttons {
  display: flex;
  gap: 1rem;
}

.merge-action {
  row-gap: 0.5rem;
  flex: 0 1 auto;
  min-width: 0;
}

::v-deep .vgt-selection-info-row {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
}

::v-deep .vgt-selection-info-row__actions {
  order: 1;
  float: none !important;
  flex: 0 1 auto;
  min-width: 0;
  margin: 0 0.75rem;
}

::v-deep .vgt-selection-info-row > a {
  order: 2;
  flex: 0 0 auto;
}

/* Farklı işletme uyarısı aktifken "xx sipariş seçildi" metin düğümünü gizle */
.merge-warn-mode ::v-deep .vgt-selection-info-row {
  font-size: 0;
}

.merge-warn-mode ::v-deep .vgt-selection-info-row > * {
  font-size: 1rem;
}

.merge-link {
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
}

.merge-link:hover {
  color: #0056b3;
}

.merge-warning {
  line-height: 1.3;
  white-space: nowrap;
}

.tab-button {
  min-width: 180px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
}

.b-sidebar .p-3 {
  padding-left: 20px;
  padding-right: 20px;
}

.b-sidebar h4 {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 10px;
}

::v-deep(.custom-sidebar .b-sidebar) {
  width: 90vw;
  max-width: 600px;
}

@media (min-width: 768px) {
  ::v-deep(.custom-sidebar .b-sidebar) {
    width: 500px;
  }
}

@media (min-width: 1200px) {
  ::v-deep(.custom-sidebar .b-sidebar) {
    width: 600px;
  }
}
.vue-good-table table {
  table-layout: auto !important;  /* içeriklere göre genişlik */
  width: 100%;
}

::v-deep(.vgt-table th.col-notes, .vgt-table td.col-notes) {
  min-width: 200px !important;
}

.qty-stepper .qty-input {
  width: 60px;
  text-align: center;
  /* Chrome, Safari, Edge */
  -moz-appearance: textfield;
}
.qty-stepper .qty-input::-webkit-outer-spin-button,
.qty-stepper .qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.qty-stepper .qty-btn {
  min-width: 32px;
  font-weight: 600;
}

</style>